import {
  DropdownMenu,
  Hyperlink,
  Tooltip,
  Typography,
} from 'br2s-ui-componentlibrary_r2_02';
import { PROFILE_TYPES } from './profileSelection-constant';
import ProfileSelectionTable from './ProfileSelectionTable';
import { toggleEntityChange } from './profileSelection-utilities';
import { LocalStorageService, useHTTPError } from 'br2s-ui-common-utils-02';
import { useStore } from '@/redux/store';
import { AuthAPI } from '@/services/api/auth';
import { useNavigate } from 'react-router-dom';
import {
  ProfileSwitchTableDataWithDropdown,
  ProfileTypeKeys,
} from '@/redux/slices/profileSwitchSlice';
import useProfileSwitch from '@/utilities/hooks/useProfileSwitch/useProfileSwitch';
import { useEffect, useMemo } from 'react';

const ProfileSelectionForm = () => {
  const { toggleLoading } = useStore();
  const { handleHTTPError } = useHTTPError();
  const {
    entities,
    selectedTabKey,
    setSelectedEntity,
    fetchProfilesIndividual,
    resetProfileSwitchState,
  } = useProfileSwitch();
  const navigate = useNavigate();

  const { uen, sourceUen } = LocalStorageService.getAuthInfo(
    'uen',
    'sourceUen',
  );

  const currentEntity = entities[
    selectedTabKey
  ] as ProfileSwitchTableDataWithDropdown;
  const currentEntitySelectedUen = currentEntity.selectedUen ?? '';
  const entityPagination = currentEntity.pagination;

  const dropdownOptions = useMemo(
    () =>
      selectedTabKey === 'myEntities' || selectedTabKey === 'officialReceiver'
        ? null
        : currentEntity.parentEntities?.map(({ uen, uenName }) => ({
            id: uen,
            value: uen,
            label: uenName,
          })),
    [selectedTabKey],
  );

  const content = {
    label: PROFILE_TYPES[selectedTabKey].header_label,
    description: PROFILE_TYPES[selectedTabKey].description,
    selectLabel: PROFILE_TYPES[selectedTabKey].selectLabel,
  };

  // istanbul ignore next
  useEffect(() => {
    if (!dropdownOptions || currentEntitySelectedUen) return;

    const selectedKey = selectedTabKey as Exclude<
      ProfileTypeKeys,
      'myEntities' | 'officialReceiver'
    >;

    let selectedUen = null;

    const checkSourceUen = ({ id }: { id: string }) => id === sourceUen;
    const checkUen = ({ id }: { id: string }) => id === uen;

    if (sourceUen && dropdownOptions?.some(checkSourceUen)) {
      selectedUen = dropdownOptions?.find(checkSourceUen)?.id || null;
    } else if (uen && dropdownOptions?.some(checkUen)) {
      selectedUen = dropdownOptions?.find(checkUen)?.id || null;
    }

    if (selectedUen) {
      setSelectedEntity(selectedKey, selectedUen ?? '');
      fetchProfilesIndividual(selectedUen, selectedTabKey, {
        itemsPerPage: entityPagination.pageSize,
        page: 0,
      });
    }
  }, [selectedTabKey, dropdownOptions]);

  // istanbul ignore next
  const regenerateCsp = async () => {
    toggleLoading(true);

    try {
      const entity = dropdownOptions?.find(
        (option) => option.id === currentEntitySelectedUen,
      );

      const response = await AuthAPI.regenerateToken({
        targetUen: entity?.id ?? '',
        targetUenName: entity?.label ?? '',
        sourceUens: [],
        sourceUenNames: [],
      });

      if (response?.status === 200) {
        const infoResponse = await AuthAPI.getSessionInfo();

        if (infoResponse?.status === 200) {
          const { result } = infoResponse.data;
          const redirectUrl = LocalStorageService.getUrlPriorToLogin();
          localStorage.setItem('exemptSwitchProfileDeny', 'true');
          LocalStorageService.setAuth(JSON.stringify(result));
          toggleEntityChange('true');
          navigate(redirectUrl ?? '/dashboard');
          LocalStorageService.clearUrlPriorToLogin();

          // Reset profile switch redux after navigate
          setTimeout(() => resetProfileSwitchState(), 0);
        }
      }
    } catch (error) {
      console.error(error);
      handleHTTPError(error, { errorType: 'inline' });
    } finally {
      toggleLoading(false);
    }
  };

  const handleDropdownChange = (value: string) => {
    setSelectedEntity(
      selectedTabKey as Exclude<
        ProfileTypeKeys,
        'myEntities' | 'officialReceiver'
      >,
      value,
    );

    fetchProfilesIndividual(value, selectedTabKey, {
      itemsPerPage: entityPagination.pageSize,
      page: 0,
    });
  };

  return (
    <div className="tabs-content">
      <div className="switch-profile-form">
        <div className="form-title">
          <Typography type="headline-4" weight="bold" label={content.label} />
        </div>
        {!!content.description && (
          <div className="form-description">
            <Typography type="subheadline" label={content.description} />
          </div>
        )}

        {selectedTabKey !== 'myEntities' && (
          <div className="form-selection col-md-7 w-100">
            {!!dropdownOptions && (
              <DropdownMenu
                value={currentEntitySelectedUen}
                label={content.selectLabel}
                id="switch-profile-select"
                options={dropdownOptions}
                onChange={handleDropdownChange}
              />
            )}
          </div>
        )}

        {selectedTabKey === 'corporateServiceProviders' &&
          !!currentEntitySelectedUen && (
            <div className="subheadline">
              Select the client that you would like to file for from the list
              below. <br /> If you are filing for an ad hoc client, proceed to
              the post-login dashboard and select the eService from the mega
              menu.
              <Tooltip
                label=""
                popupPosition="top"
                popupText="Individual/entity not on the authorised client list able to appoint the CSP for incorporation, charges or PA renewal."
              />
              <br />
              <span
                onClick={(e) => {
                  e.preventDefault();
                  regenerateCsp();
                }}
              >
                <Hyperlink
                  id="csp-link"
                  fontStyle="subheadline"
                  label="Proceed to dashboard"
                  href="#"
                />
              </span>
            </div>
          )}
      </div>
      {(!!currentEntitySelectedUen ||
        ['myEntities', 'officialReceiver'].includes(selectedTabKey)) && (
        <ProfileSelectionTable />
      )}
    </div>
  );
};

export default ProfileSelectionForm;
